import { Share2, Facebook, Linkedin, Twitter, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";

export const SocialShare = ({ className }: { className?: string }) => {
  const { toast } = useToast();
  const shareUrl = "https://skylofiatc.lovable.app/";
  const shareText = "Check out Sky Lofi ATC - Lofi beats with live air traffic control radio. Perfect for studying and relaxing! 🎵✈️";

  const shareLinks = [
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-[#1DA1F2]"
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-[#1877F2]"
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      color: "hover:text-[#0A66C2]"
    }
  ];

  const handleShare = (url: string) => {
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "Share it with your friends ✈️",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Sheet>
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className={`glass-card hover:scale-105 smooth-transition ${className || ""}`}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </SheetTrigger>
          </TooltipTrigger>
          <TooltipContent>
            Share Sky Lofi ATC with others
          </TooltipContent>
        </Tooltip>

        <SheetContent side="right" className="glass-card border-white/10">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold">Share Sky Lofi ATC</SheetTitle>
            <SheetDescription>
              Help spread the word about this free, ad-free lofi streaming experience!
            </SheetDescription>
          </SheetHeader>

          <div className="mt-8 space-y-4">
            <Button
              variant="outline"
              className="w-full justify-start glass-card hover:scale-105 smooth-transition hover:text-foreground"
              onClick={handleCopyLink}
            >
              <Copy className="h-5 w-5 mr-3" />
              <span>Copy Link</span>
            </Button>
            
            {shareLinks.map((platform) => (
              <Button
                key={platform.name}
                variant="outline"
                className="w-full justify-start glass-card hover:scale-105 smooth-transition hover:text-foreground group"
                onClick={() => handleShare(platform.url)}
              >
                <platform.icon className={`h-5 w-5 mr-3 transition-colors ${platform.color}`} />
                <span>Share on {platform.name}</span>
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};
